import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
} from '@material-ui/core';
import { Close, Info } from '@material-ui/icons';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Snackbar,
  Stack,
  useResponsiveValue,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { type ChangeEvent, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import {
  claimLoadsImportDropEvent,
  validateLoadsImportFile,
} from '../core/LoadsImportZone';
import { UploadDispatchSheetCardButton } from '../core/UploadDispatchSheetCardButton';
import { useGeneralSettings } from 'carrier/settings/data/SettingsAPI';
import { useClipboard } from 'shared/helpers/DomHelpers';
import { LocalStore, useLocalStore } from 'shared/helpers/Store';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { EditDocumentIcon } from 'shared/icons/EditDocumentIcon';
import styled from 'styled-components';
import { ForwardToInboxIcon } from 'shared/icons/ForwardToInboxIcon';
import { ContentCopyIcon, UploadFileIcon, XmlIcon } from 'shared/icons';
import { InlineBulletItems } from 'shared/ui/InlineBulletItems';
import { LoadsImportErrorDialog } from './core/LoadsImportErrorDialog';
import { useLoadsImportFileInput } from './core/useLoadsImportFileInput';

const OtherWaysCard = styled.div<{ button?: boolean }>`
  border: 1px solid ${ColorDynamic.Silver400};
  border-radius: 4px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 112px;

  ${({ button }) =>
    button &&
    `
    cursor: pointer;
    &:hover {
      background-color: ${ColorDynamic.Blue50};
      transition: background-color 0.2s ease;
    }
  `}
`;

const OtherWaysCardIconContainer = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${ColorDynamic.Blue50};
  border-radius: 50%;
  flex-shrink: 0;
`;

const OtherWaysCardBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const ImportEmailLine = styled.div`
  min-width: 0;
`;

const ImportEmailText = styled(TextBox)`
  display: inline;
  word-break: break-all;
`;

const ImportEmailCopy = styled.div`
  display: inline-flex;
  vertical-align: middle;
  margin-left: 4px;
`;

const DISPATCH_SHEET_HELP_URL =
  'https://support.superdispatch.com/hc/en-us/articles/35622600792339-Import-PDF-Dispatch-Sheets-CSV-Loads-Inside-Carrier-TMS#supported-dispatch-sheets';
const IMPORT_API_HELP_URL =
  'https://support.superdispatch.com/hc/en-us/articles/35670927440659-API-Integrations#h_01KKVKYBKXQWPEQC8R0PPFXYQ6';
const ADD_LOAD_DRAG_DROP_TIP_DISMISSED_KEY =
  'loads-add-load-drag-drop-tip-dismissed';

interface LoadsAddLoadDialogProps {
  open: boolean;
  onClose: () => void;
}

export function LoadsAddLoadDialog({ open, onClose }: LoadsAddLoadDialogProps) {
  const navigate = useNavigate();
  const { copy } = useClipboard();
  const { addSnackbar } = useSnackbarStack();
  const tipDismissed = useLocalStore(ADD_LOAD_DRAG_DROP_TIP_DISMISSED_KEY);
  const isTipVisible = tipDismissed !== '1';
  const { data: generalSettings } = useGeneralSettings({ enabled: open });
  const isMobile = useResponsiveValue(true, false);
  const {
    clearLegacyImportError,
    handleFileInputChange,
    inputMultiple,
    isSubmitting,
    legacyImportErrorMessage,
    legacyImportFiles,
    queueFilesForImport,
    shouldShowDropzone,
  } = useLoadsImportFileInput({
    utmMedium: 'add-load-dialog',
  });

  const importDroppedFiles = useCallback(
    (fileList: FileList | File[]) => {
      const { didQueue, rejectedFiles } = queueFilesForImport(fileList);

      if (!didQueue) {
        const message = rejectedFiles[0]?.errors[0]?.message;

        if (message) {
          addSnackbar(message, { variant: 'error' });
        }

        return;
      }

      onClose();
    },
    [addSnackbar, onClose, queueFilesForImport],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    open: openFileDialog,
  } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
    onDrop: (files, _rejectedFiles, event) => {
      if (!claimLoadsImportDropEvent(event)) {
        return;
      }

      importDroppedFiles(files);
    },
    validator: validateLoadsImportFile,
    multiple: inputMultiple,
    noClick: true,
    noKeyboard: true,
    disabled: !open,
  });

  useEffect(() => {
    if (!open || !shouldShowDropzone) {
      return;
    }

    function handleWindowDrop(event: DragEvent) {
      event.preventDefault();

      if (!claimLoadsImportDropEvent(event)) {
        return;
      }

      const fileList = event.dataTransfer?.files;

      if (!fileList?.length) {
        return;
      }

      importDroppedFiles(fileList);
    }

    function handleDragOver(event: DragEvent) {
      event.preventDefault();
    }

    window.addEventListener('drop', handleWindowDrop);
    window.addEventListener('dragover', handleDragOver);

    return () => {
      window.removeEventListener('drop', handleWindowDrop);
      window.removeEventListener('dragover', handleDragOver);
    };
  }, [open, shouldShowDropzone, importDroppedFiles]);

  function handleFillManually() {
    trackLoadsEvent({
      name: 'Carrier Clicked Fill In Manually',
    });
    onClose();
    navigate('/loads/add');
  }

  function handleCopyImportEmail() {
    const inbox = generalSettings?.inbox;
    if (!inbox) return;

    copy(inbox);
    addSnackbar('Email copied!', { variant: 'success' });
    trackLoadsEvent({
      name: 'Carrier Copied Import Email',
    });
  }

  function handleUploadCardClick() {
    trackLoadsEvent({
      name: 'Carrier Clicked Upload Dispatch Sheet',
    });
    openFileDialog();
  }

  function handleUploadInputChange(event: ChangeEvent<HTMLInputElement>) {
    const { didQueue, rejectedFiles } = handleFileInputChange(event);

    if (didQueue) {
      onClose();
      return;
    }

    const message = rejectedFiles[0]?.errors[0]?.message;

    if (message) {
      addSnackbar(message, { variant: 'error' });
      event.target.value = '';
    }
  }

  return (
    <>
      {!!legacyImportFiles.length && (
        <LoadsImportErrorDialog
          files={legacyImportFiles}
          errorMessage={legacyImportErrorMessage}
          onClose={clearLegacyImportError}
        />
      )}

      <Snackbar open={isSubmitting}>Importing dispatch sheet…</Snackbar>

      <Dialog
        fullWidth={true}
        maxWidth="md"
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Columns align="center">
            <Column width="fluid">
              <TextBox variant="heading-3">Add Load</TextBox>
            </Column>
            <Column width="content">
              <IconButton aria-label="close" size="small" onClick={onClose}>
                <Close />
              </IconButton>
            </Column>
          </Columns>
        </DialogTitle>
        <Divider />

        <DialogContent>
          <Box
            padding={['none', 'small']}
            paddingTop={['medium', 'large']}
            paddingBottom={['none', 'medium']}
          >
            <Stack space="xsmall">
              {isTipVisible && (
                <Box
                  backgroundColor="Blue50"
                  padding="xsmall"
                  paddingTop="xxsmall"
                  paddingBottom="xxsmall"
                  alignItems="center"
                  justifyContent="space-between"
                  display="flex"
                  minHeight="40px"
                  borderRadius="small"
                >
                  <Box display="flex" alignItems="center">
                    <Box paddingRight="xsmall">
                      <Info color="primary" fontSize="small" />
                    </Box>

                    <TextBox variant="body">
                      <strong>Tip:</strong> You can drag & drop dispatch sheets
                      directly onto the Loads page. No need to open this dialog.
                    </TextBox>
                  </Box>
                  <IconButton
                    aria-label="close"
                    size="small"
                    color="primary"
                    onClick={() => {
                      LocalStore.set(ADD_LOAD_DRAG_DROP_TIP_DISMISSED_KEY, '1');
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
              )}

              <div {...getRootProps()}>
                <input
                  {...getInputProps()}
                  data-testid="add-load-dialog-import-input"
                  onChange={handleUploadInputChange}
                />

                <UploadDispatchSheetCardButton
                  size="large"
                  $isDragActive={isDragActive}
                  $isDragReject={isDragReject}
                  onClick={handleUploadCardClick}
                >
                  <Stack space="xxsmall" align="center">
                    <Inline verticalAlign="center">
                      <Box fontSize="larger">
                        <UploadFileIcon color="primary" fontSize="inherit" />
                      </Box>
                      <TextBox variant="heading-4" color="blue">
                        {isDragActive
                          ? isDragReject
                            ? 'This file type is not supported'
                            : 'Drop PDF or CSV to Import Loads'
                          : 'Upload Dispatch Sheet'}
                      </TextBox>
                    </Inline>
                    <TextBox color="secondary" align="center">
                      {isDragActive
                        ? isDragReject
                          ? 'Only PDF and CSV files are allowed'
                          : 'Release to import your dispatch sheet'
                        : "Drag & drop PDF or CSV file and we'll fill in everything for you"}
                    </TextBox>
                  </Stack>
                </UploadDispatchSheetCardButton>
              </div>

              <TextBox color="secondary" align="center" variant="hint">
                Supports{' '}
                <Link
                  href={DISPATCH_SHEET_HELP_URL}
                  target="_blank"
                  rel="noopener"
                  color="textSecondary"
                >
                  most dispatch sheets
                </Link>
                .
              </TextBox>

              <Box paddingTop="medium" paddingBottom="medium">
                <Columns align="center" space="small">
                  <Column>
                    <Box width="50%" marginRight="none" marginLeft="auto">
                      <Divider />
                    </Box>
                  </Column>
                  <Column width="content">
                    <TextBox color="disabled" variant="heading-6">
                      OTHER WAYS TO ADD
                    </TextBox>
                  </Column>
                  <Column>
                    <Box width="50%" marginRight="auto" marginLeft="none">
                      <Divider />
                    </Box>
                  </Column>
                </Columns>
              </Box>

              <Columns space="medium" collapseBelow="tablet">
                <Column>
                  <OtherWaysCard button={true} onClick={handleFillManually}>
                    <OtherWaysCardIconContainer>
                      <EditDocumentIcon color="primary" />
                    </OtherWaysCardIconContainer>
                    <OtherWaysCardBody>
                      <Stack space="xxsmall" align="left">
                        <TextBox
                          display="block"
                          variant="heading-4"
                          align="left"
                        >
                          Fill In Manually
                        </TextBox>
                        <TextBox display="block" color="secondary" align="left">
                          Enter all details yourself
                        </TextBox>
                      </Stack>
                    </OtherWaysCardBody>
                  </OtherWaysCard>
                </Column>
                <Column>
                  <OtherWaysCard button={true} onClick={handleCopyImportEmail}>
                    <OtherWaysCardIconContainer>
                      <ForwardToInboxIcon color="primary" />
                    </OtherWaysCardIconContainer>
                    <OtherWaysCardBody>
                      <Stack space="xxsmall">
                        <TextBox variant="heading-4">Import Via Email</TextBox>
                        <TextBox color="secondary">
                          Send PDF or CSV to your import email:
                        </TextBox>
                        {generalSettings?.inbox && (
                          <ImportEmailLine>
                            <ImportEmailText
                              variant="body-semibold"
                              color="secondary"
                            >
                              {generalSettings.inbox}
                            </ImportEmailText>
                            <ImportEmailCopy>
                              <ContentCopyIcon
                                fontSize="small"
                                color="action"
                              />
                            </ImportEmailCopy>
                          </ImportEmailLine>
                        )}
                      </Stack>
                    </OtherWaysCardBody>
                  </OtherWaysCard>
                </Column>
              </Columns>
            </Stack>
          </Box>
        </DialogContent>
        <Box
          backgroundColor="Silver200"
          minHeight="54px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Inline verticalAlign="center">
            <XmlIcon fontSize="small" htmlColor={ColorDynamic.Dark300} />
            <InlineBulletItems
              horizontalAlign="center"
              verticalAlign="center"
              space="xsmall"
            >
              <TextBox>Import is also available via API</TextBox>
              <Link href={IMPORT_API_HELP_URL} target="_blank">
                Learn more
              </Link>
            </InlineBulletItems>
          </Inline>
        </Box>
      </Dialog>
    </>
  );
}
